import { api } from '@/services/api'

/**
 * Small wrapper around the `/waitlist` endpoints. Used by the roadmap /
 * coming-soon modal to:
 *   - Read the public count for a feature (drives the copy variant at 200+).
 *   - Join the waitlist and opt into launch-email notifications.
 *
 * `POST /waitlist/notify-all` is admin-only on the backend and deliberately
 * not exposed here — launch-email blasts are triggered by an ops script /
 * cron, not from the UI.
 */

export type WaitlistCountResponse = {
  feature_id: string
  count: number
}

type CountEnvelope = {
  success: boolean
  data: WaitlistCountResponse
}

export async function fetchWaitlistCount(featureId: string): Promise<number> {
  const { data } = await api.get<CountEnvelope>(
    `/waitlist/${encodeURIComponent(featureId)}/count`,
  )
  return data?.data?.count ?? 0
}

export type WaitlistMyStatus = {
  joined: boolean
  notify_me: boolean
}

type MyStatusEnvelope = {
  success: boolean
  data: WaitlistMyStatus & { feature_id: string }
}

/**
 * Read-only fetch of the caller's own waitlist state for a feature. Lets the
 * UI show "Already on the list" on a re-open without invoking the join POST,
 * which would otherwise auto-add them just for opening the modal.
 */
export async function fetchMyWaitlistStatus(
  featureId: string,
): Promise<WaitlistMyStatus> {
  const { data } = await api.get<MyStatusEnvelope>(
    `/waitlist/${encodeURIComponent(featureId)}/me`,
  )
  return {
    joined: Boolean(data?.data?.joined),
    notify_me: Boolean(data?.data?.notify_me),
  }
}

export type JoinWaitlistResult = {
  already_joined: boolean
  message: string
  position?: number
}

type JoinEnvelope = {
  success: boolean
  data: JoinWaitlistResult
}

export async function joinWaitlist(
  featureId: string,
): Promise<JoinWaitlistResult> {
  const { data } = await api.post<JoinEnvelope>('/waitlist', {
    feature_id: featureId,
  })
  return data.data
}

export type ToggleNotifyResult = {
  notify_me: boolean
  message: string
}

type ToggleEnvelope = {
  success: boolean
  data: ToggleNotifyResult
}

export async function toggleWaitlistNotify(
  featureId: string,
): Promise<ToggleNotifyResult> {
  const { data } = await api.patch<ToggleEnvelope>('/waitlist/notify', {
    feature_id: featureId,
  })
  return data.data
}

/**
 * Convenience: join the waitlist (if not already joined) AND ensure notify_me
 * is on. The modal's "Notify Me" button calls this — a single click should
 * guarantee both "on the list" and "will be emailed at launch" regardless of
 * prior state.
 */
export async function joinWaitlistAndNotify(
  featureId: string,
): Promise<{ notify_me: boolean; alreadyOnList: boolean }> {
  const joined = await joinWaitlist(featureId)

  // If the user wasn't on the list before, toggleNotify will flip false → true.
  // If they were already on the list with notify_me already true, we skip to
  // avoid flipping it off. We have no read endpoint for their own notify_me
  // state yet, so we assume: fresh join → notify_me is false (default) → toggle.
  // Already-joined → leave notify alone (user may have deliberately turned it off).
  if (!joined.already_joined) {
    const toggled = await toggleWaitlistNotify(featureId)
    return { notify_me: toggled.notify_me, alreadyOnList: false }
  }

  return { notify_me: true, alreadyOnList: true }
}
